// Generated by tutorials/record.cjs from tutorials/articles/h64-auto-tray-line-setup.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h64 --text-only` (wording) or
// `node tutorials/record.cjs h64` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'auto-tray-line-setup',
  catalogId: 'h64',
  title: 'Setting up the tray line automatically',
  summary: 'While your facility is being set up, the tray line auto setup builds your carts, puts the rooms in tray order and gives every resident a place, in nine short steps, with an undo.',
  audience: 'Dietary managers and administrators setting up a facility that is new to Dietech Online',
  area: 'kitchen',
  learn: [
    'Where to find the auto setup: Getting Started and Tray Line Setup',
    'Choose how trays go out at each meal, and the carts',
    'Put the carts and the rooms on each cart in order',
    'See who is left off, and link each unit to its cart',
    'Check the preview, set up the tray line and check the result',
    'Undo the last auto setup, or fine-tune it on Tray Line Setup',
  ],
  keywords: [
    'tray line',
    'auto setup',
    'tray line auto setup',
    'automatic tray line',
    'carts',
    'cart',
    'set',
    'tray order',
    'room order',
    'split cart',
    'combine carts',
    'dining room',
    'unit mapping',
    'link unit',
    'placement',
    'undo',
    'getting started',
    'units rooms and tray line',
    'new facility',
  ],
  steps: [
    {
      id: 'getting-started',
      part: 'Find the auto setup',
      heading: 'In Getting Started',
      text: 'In Getting Started, the step Units, rooms and tray line is the auto setup. It starts from your intake answers: if you said you send out four carts, it already has four.\n\nAdd your units and their rooms and beds on Unit Rooms first. The auto setup places the beds it finds there.',
      media: {
        image: {
          src: '/help/auto-tray-line-setup/getting-started.webp',
          width: 1280,
          height: 1026,
        },
        alt: 'Getting Started on Units, rooms and tray line: add your units on Unit Rooms first, then the tray line auto setup with its nine steps on the left and Step 1 of 9, How trays go out.',
      },
    },
    {
      id: 'tray-line-setup',
      part: 'Find the auto setup',
      heading: 'Or on Tray Line Setup',
      text: 'You can also open it from Kitchen, Tray Line Setup: select Auto setup at the top right.\n\nFrom here it starts with one cart per unit, named after the unit.',
      media: {
        image: {
          src: '/help/auto-tray-line-setup/tray-line-setup.webp',
          width: 1280,
          height: 324,
        },
        alt: 'Tray Line Setup with the Room/Bed Setup and Resident Assignment tabs, and the Auto setup button at the top right.',
      },
    },
    {
      id: 'nine-steps',
      part: 'Find the auto setup',
      heading: 'Nine short steps',
      text: 'The auto setup opens on step 1. The nine steps are listed on the left: How trays go out, Carts, Cart order, Room order, Who is left off, Automatic placement, Preview, Apply and Check.\n\nContinue and Previous move through them, or select a step in the list. Nothing is changed until you apply it on step 8.',
      media: {
        image: {
          src: '/help/auto-tray-line-setup/nine-steps.webp',
          width: 1280,
          height: 434,
        },
        clip: {
          src: '/help/auto-tray-line-setup/nine-steps.mp4',
          width: 1280,
          height: 434,
          durationSec: 2,
        },
        alt: 'The Tray line auto setup window with the nine steps listed on the left and Step 1 of 9, How trays go out, on the right.',
      },
    },
    {
      id: 'how-trays',
      part: 'Meals and carts',
      heading: '1. How trays go out',
      text: 'Pick how trays reach your residents: Carts to the rooms, The dining room, Both (carts to the rooms and a dining room), or No trays for this meal.\n\nThe same for every meal is ticked to start. Untick it to choose for breakfast, lunch and dinner one by one.',
      media: {
        image: {
          src: '/help/auto-tray-line-setup/how-trays.webp',
          width: 1280,
          height: 542,
        },
        alt: 'Step 1, How trays go out: The same for every meal ticked, and Every meal set to Carts to the rooms.',
      },
    },
    {
      id: 'each-meal',
      part: 'Meals and carts',
      heading: 'A different way for one meal',
      text: 'Here lunch is served in the dining room as well as in the rooms, so Lunch is set to Both. Then pick the Dining room.\n\nWith Both, the dining room\'s set starts empty: move the residents who eat there on Tray Line Setup afterwards.',
      media: {
        image: {
          src: '/help/auto-tray-line-setup/each-meal.webp',
          width: 1280,
          height: 676,
        },
        clip: {
          src: '/help/auto-tray-line-setup/each-meal.mp4',
          width: 1280,
          height: 676,
          durationSec: 4.9,
        },
        alt: 'The same for every meal unticked, Breakfast and Dinner on Carts to the rooms, Lunch on Both, and Main Dining Room picked as the dining room.',
      },
    },
    {
      id: 'carts',
      part: 'Meals and carts',
      heading: '2. Carts',
      text: 'Step 2 starts with one cart per unit, named after the unit, with how many rooms each one takes. Type over a name to change it.\n\nCombine with next puts a small unit on the same cart as the one below it; Separate takes them apart again.',
      media: {
        image: {
          src: '/help/auto-tray-line-setup/carts.webp',
          width: 1280,
          height: 552,
        },
        clip: {
          src: '/help/auto-tray-line-setup/carts.mp4',
          width: 1280,
          height: 552,
          durationSec: 2.4,
        },
        alt: 'Step 2, Carts: Cart 1 Maple (12 rooms), Cart 2 Birch (12 rooms) and Cart 3 Willow (16 rooms), each with a Split choice and Combine with next.',
      },
    },
    {
      id: 'split',
      part: 'Meals and carts',
      heading: 'Split a big unit',
      text: 'Willow has 16 rooms, too many for one cart. Pick 2 carts next to it and select Split. Willow 1 takes rooms 125 to 132 and Willow 2 rooms 133 to 140.\n\nPut Willow back on one cart undoes it. Start again with one cart per unit throws away every change on this step.',
      media: {
        image: {
          src: '/help/auto-tray-line-setup/split.webp',
          width: 1280,
          height: 690,
        },
        clip: {
          src: '/help/auto-tray-line-setup/split.mp4',
          width: 1280,
          height: 690,
          durationSec: 2.4,
        },
        alt: 'Willow split into Willow 1, rooms 125 to 132, and Willow 2, rooms 133 to 140, with Put Willow back on one cart below the list.',
      },
    },
    {
      id: 'cart-order',
      part: 'Cart and room order',
      heading: '3. Cart order',
      text: 'Put the carts in the order they leave the kitchen. Use the up and down arrows, or drag a cart by its handle. The first cart is Cart 1 on the tray tickets.\n\nHere Birch goes out first, so it is moved to the top.',
      media: {
        image: {
          src: '/help/auto-tray-line-setup/cart-order.webp',
          width: 1280,
          height: 480,
        },
        clip: {
          src: '/help/auto-tray-line-setup/cart-order.mp4',
          width: 1280,
          height: 480,
          durationSec: 3.7,
        },
        alt: 'Step 3, Cart order: Birch moved to the top, then Maple, Willow 1 and Willow 2, each with up and down arrows.',
      },
    },
    {
      id: 'room-order',
      part: 'Cart and room order',
      heading: '4. Room order',
      text: 'Pick a cart under Cart, then the order its trays go out: Room number low to high, or High to low. Beds in a room go A before B.\n\nTo set your own order, move the rooms with the arrows or drag them; the choice changes to My own order.',
      media: {
        image: {
          src: '/help/auto-tray-line-setup/room-order.webp',
          width: 1280,
          height: 806,
        },
        clip: {
          src: '/help/auto-tray-line-setup/room-order.mp4',
          width: 1280,
          height: 806,
          durationSec: 4.6,
        },
        alt: 'Step 4, Room order: cart 4, Willow 2, picked, High to low chosen, and its rooms listed from 140 A, B down to 133 A, B.',
      },
    },
    {
      id: 'left-off',
      part: 'Who gets a place',
      heading: '5. Who is left off',
      text: 'Residents who do not get a tray from the tray line keep their place as it is: tube feed only, NPO, Bulk Order, Bulk 1st Choice, and templates. Only the counts are shown.\n\nIf anything in your plan must be fixed first, such as a cart with no name, it is listed here under Fix these first.',
      media: {
        image: {
          src: '/help/auto-tray-line-setup/left-off.webp',
          width: 1280,
          height: 448,
        },
        clip: {
          src: '/help/auto-tray-line-setup/left-off.mp4',
          width: 1280,
          height: 448,
          durationSec: 2.8,
        },
        alt: 'Step 5, Who is left off: tube feed only (NPO) 1, NPO 1, Bulk Order 0, Bulk 1st Choice 0 and templates 2.',
      },
    },
    {
      id: 'placement',
      part: 'Who gets a place',
      heading: '6. Automatic placement',
      text: 'Leave Link each unit to its cart ticked. Then a new resident goes on the end of their unit\'s cart, and a resident who moves to another unit goes on the end of the new unit\'s cart, without anyone setting it by hand.\n\nA split unit is linked to its first cart. If unit mapping is off in your facility settings, the step offers to turn it on.',
      media: {
        image: {
          src: '/help/auto-tray-line-setup/placement.webp',
          width: 1280,
          height: 548,
        },
        clip: {
          src: '/help/auto-tray-line-setup/placement.mp4',
          width: 1280,
          height: 548,
          durationSec: 2.3,
        },
        alt: 'Step 6, Automatic placement: Link each unit to its cart ticked, Birch to Birch, Maple to Maple and Willow to Willow 1, for Breakfast, Lunch and Dinner, and a note that Willow is split over carts.',
      },
    },
    {
      id: 'preview',
      part: 'Preview and apply',
      heading: '7. Preview',
      text: 'The preview counts what would happen: residents getting a place for the first time, residents who would move, those already in the right place, residents with no place, and beds on no cart.\n\nBelow, each cart lists its trays in order. Select a cart to open it.',
      media: {
        image: {
          src: '/help/auto-tray-line-setup/preview.webp',
          width: 1280,
          height: 816,
        },
        clip: {
          src: '/help/auto-tray-line-setup/preview.mp4',
          width: 1280,
          height: 816,
          durationSec: 3.8,
        },
        alt: 'Step 7, Preview: 3 residents getting a place for the first time, 28 who would move, none with no place and no beds on no cart, and the four carts, 1. Birch open with its trays from 113 A.',
      },
    },
    {
      id: 'apply',
      part: 'Preview and apply',
      heading: '8. Apply',
      text: 'Step 8 sums it up: the carts added, changed and removed, the beds written on the room tray line, and how many residents get a new place. Everything is written at once.\n\nSelect Set up the tray line. You can undo it afterwards.',
      media: {
        image: {
          src: '/help/auto-tray-line-setup/apply.webp',
          width: 1280,
          height: 336,
        },
        clip: {
          src: '/help/auto-tray-line-setup/apply.mp4',
          width: 1280,
          height: 336,
          durationSec: 2.7,
        },
        alt: 'Step 8, Apply: 4 carts added and 9 changed, 80 beds written on the room tray line and 31 residents given a new place, and the Set up the tray line button.',
      },
    },
    {
      id: 'check',
      part: 'Preview and apply',
      heading: '9. Check',
      text: 'When it is done, step 9 checks the result: tray line warnings, beds on no cart and residents with no place. A green line means every bed is on a cart and no resident has a tray line warning.\n\nThen print one meal\'s tray tickets and walk them along the carts, or fine-tune a room or a resident on Tray Line Setup.',
      media: {
        image: {
          src: '/help/auto-tray-line-setup/check.webp',
          width: 1280,
          height: 416,
        },
        clip: {
          src: '/help/auto-tray-line-setup/check.mp4',
          width: 1280,
          height: 416,
          durationSec: 2.5,
        },
        alt: 'Step 9, Check: The tray line is set up, every bed is on a cart and no resident has a tray line warning, with 0 warnings, 0 beds on no cart and 0 residents with no place, and links to print tray tickets and to Tray Line Setup.',
      },
    },
    {
      id: 'undo',
      part: 'Undo or fine-tune',
      heading: 'Undo the last auto setup',
      text: 'Changed your mind? Go back to step 8, Apply, and select Undo last auto setup. It says when the last auto setup was made and by whom.\n\nThe question lists anything changed on the tray line since then: Undo puts that back too.',
      media: {
        image: {
          src: '/help/auto-tray-line-setup/undo.webp',
          width: 708,
          height: 350,
        },
        clip: {
          src: '/help/auto-tray-line-setup/undo.mp4',
          width: 708,
          height: 350,
          durationSec: 2,
        },
        alt: 'The question Undo the last auto setup? The carts, the room tray line and every resident it moved go back to how they were before it, with Cancel and Undo.',
      },
    },
    {
      id: 'undone',
      part: 'Undo or fine-tune',
      heading: 'Put back as it was',
      text: 'Select Undo. The carts, the room tray line and every resident the auto setup moved go back to how they were, and a line says how many residents were put back.\n\nOnly the last auto setup can be undone.',
      media: {
        image: {
          src: '/help/auto-tray-line-setup/undone.webp',
          width: 1280,
          height: 414,
        },
        clip: {
          src: '/help/auto-tray-line-setup/undone.mp4',
          width: 1280,
          height: 414,
          durationSec: 2.7,
        },
        alt: 'Step 8 after the undo: The last auto setup was undone: 31 residents put back where they were, and the last auto setup marked undone.',
      },
    },
    {
      id: 'fine-tune',
      part: 'Undo or fine-tune',
      heading: 'Fine-tune on Tray Line Setup',
      text: 'Select Close. Tray Line Setup shows the tray line as it is now. On Room/Bed Setup, pick the meal and the set to move a room or add one that is not on a cart; on Resident Assignment, change one resident\'s place.\n\nSee Setting up the tray line for both tabs.',
      media: {
        image: {
          src: '/help/auto-tray-line-setup/fine-tune.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/auto-tray-line-setup/fine-tune.mp4',
          width: 1280,
          height: 842,
          durationSec: 3.1,
        },
        alt: 'Tray Line Setup on Room/Bed Setup after closing the auto setup, with the Assigned and Unassigned lists.',
      },
    },
  ],
  tips: [
    'Nothing changes until you select Set up the tray line on step 8. You can move back and forth through the steps, and change your mind, as often as you like.',
    'Split cuts a unit into carts by room number, sharing the beds out as evenly as the rooms allow. Put the unit back on one cart undoes a split.',
    'After an auto setup, print one meal\'s tray tickets and walk them along the carts. It is the quickest check that the order matches your building.',
  ],
  notes: [
    'The auto setup is offered while your facility is being set up with Getting Started. Once setup is signed off, change the tray line on Tray Line Setup.',
    'Setting up the tray line needs Tray Line level 4 on your role. Without it you can look through the steps, but not apply them. Ask your administrator.',
    'Only one person can change the tray line at a time. If someone else is editing it, the auto setup says so and waits.',
    'The auto setup works from the units, rooms and beds on Unit Rooms. Add them there first.',
  ],
  related: ['tray-line-setup', 'getting-started', 'tray-tickets', 'admit-resident', 'facility-setup'],
  recorded: '2026-11-03',
};

export default article;
